import { useState, useEffect } from "react";
import { authFetch } from "../api";

const TODAY      = new Date().toLocaleDateString("en-CA");
const TZ_OFFSET  = -new Date().getTimezoneOffset(); // e.g. 330 for IST

// Default range: last 7 days
const sevenDaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return d.toLocaleDateString("en-CA");
};

const ALL_HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));

const AGE_LABELS = {
  under_18: "< 18", "18_30": "18–30", "31_45": "31–45",
  "46_60": "46–60", "60_plus": "60+",
};
const GENDER_LABELS  = { male: "Male", female: "Female", other: "Other" };
const GENDER_COLORS  = { male: "#0093dd", female: "#e83e8c", other: "#6f42c1" };
const AGE_COLORS     = { under_18: "#fd7e14", "18_30": "#20c997", "31_45": "#0093dd", "46_60": "#6f42c1", "60_plus": "#e83e8c" };

const PAGE_SIZE = 5;

export default function FootfallDashboard({ centers: allCenters, apiBase, onClose }) {
  const centers = allCenters.filter(c => c.is_open !== false);
  const [fromDate, setFromDate]   = useState(TODAY);
  const [toDate, setToDate]       = useState(TODAY);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState("");
  const [page, setPage]           = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const res = await authFetch(
          `${apiBase}/footfall/summary/all?from_date=${fromDate}&to_date=${toDate}&tz_offset_minutes=${TZ_OFFSET}`,
          { signal }
        );
        if (res.ok) setSummaries(await res.json());
      } catch (e) {
        if (e.name !== "AbortError") console.error(e);
      }
      if (!signal.aborted) setLoading(false);
    };

    fetchAll();
    return () => controller.abort();
  }, [fromDate, toDate]);

  // Reset page when search or date range changes
  useEffect(() => { setPage(0); }, [search, fromDate, toDate]);

  const filteredCenters = centers
    .filter(c => summaries[c.id]?.total > 0)
    .filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.address.toLowerCase().includes(search.toLowerCase()));
  const totalPages    = Math.ceil(filteredCenters.length / PAGE_SIZE);
  const pagedCenters  = filteredCenters.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const grandTotal    = Object.values(summaries).reduce((s, v) => s + (v?.total ?? 0), 0);

  // Build combined daily totals across all centers
  const combinedByDate = {};
  Object.values(summaries).forEach(s => {
    Object.entries(s?.by_date || {}).forEach(([date, count]) => {
      combinedByDate[date] = (combinedByDate[date] || 0) + count;
    });
  });
  const sortedDates    = Object.keys(combinedByDate).sort();
  const maxDayCount    = Math.max(1, ...Object.values(combinedByDate));

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Footfall Dashboard</div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
              {fromDate === toDate ? fromDate : `${fromDate} → ${toDate}`}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>From</label>
            <input type="date" value={fromDate} max={toDate}
              onChange={e => setFromDate(e.target.value)} style={styles.datePicker} />
            <label style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>To</label>
            <input type="date" value={toDate} min={fromDate} max={TODAY}
              onChange={e => setToDate(e.target.value)} style={styles.datePicker} />
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          </div>
        </div>

        <div style={styles.body}>

          {/* Grand total + quick range buttons */}
          <div style={styles.totalBanner}>
            <div style={{ display: "flex", alignItems: "baseline" }}>
              <span style={{ fontSize: 36, fontWeight: 800, color: "#0093dd" }}>{grandTotal}</span>
              <span style={{ fontSize: 14, color: "#555", marginLeft: 10 }}>
                total visitors across {centers.length} center{centers.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { label: "Today",   from: TODAY,          to: TODAY },
                { label: "7 days",  from: sevenDaysAgo(), to: TODAY },
                { label: "30 days", from: daysAgo(29),    to: TODAY },
              ].map(r => (
                <button
                  key={r.label}
                  onClick={() => { setFromDate(r.from); setToDate(r.to); }}
                  style={styles.rangeBtn(fromDate === r.from && toDate === r.to)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          {!loading && centers.length > 0 && (
            <input
              type="search"
              placeholder="Search centers…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={styles.searchInput}
            />
          )}

          {loading && <div style={styles.empty}>Loading…</div>}

          {/* Day-by-day trend */}
          {!loading && sortedDates.length > 1 && (
            <div style={styles.card}>
              <div style={styles.sectionLabel}>Daily Trend</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 4, overflowX: "auto", paddingBottom: 4 }}>
                {sortedDates.map(date => {
                  const count = combinedByDate[date] || 0;
                  const pct   = Math.round((count / maxDayCount) * 100);
                  return (
                    <div key={date} style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 36 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#333", marginBottom: 3 }}>{count}</div>
                      <div style={{ width: 28, height: Math.max(4, pct * 0.8), background: "#0093dd", borderRadius: "3px 3px 0 0" }} />
                      <div style={{ fontSize: 10, color: "#888", marginTop: 4, writingMode: "vertical-rl", transform: "rotate(180deg)", lineHeight: 1 }}>
                        {date.slice(5)} {/* MM-DD */}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!loading && grandTotal === 0 && (
            <div style={styles.empty}>No entries for this date range.</div>
          )}

          {!loading && grandTotal > 0 && filteredCenters.length === 0 && (
            <div style={styles.empty}>No centers match "{search}".</div>
          )}

          {/* Per-center cards */}
          {!loading && pagedCenters.map(c => (
            <CenterCard key={c.id} center={c} summary={summaries[c.id]} />
          ))}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                onClick={() => setPage(p => p - 1)}
                disabled={page === 0}
                style={styles.pageBtn(page === 0)}
              >
                ← Prev
              </button>
              <span style={{ fontSize: 13, color: "#555" }}>
                Page {page + 1} of {totalPages} · {filteredCenters.length} centers
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                style={styles.pageBtn(page >= totalPages - 1)}
              >
                Next →
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function CenterCard({ center, summary }) {
  const byHour     = summary.by_hour      || {};
  const byGender   = summary.by_gender    || {};
  const byAge      = summary.by_age_group || {};
  const total      = summary.total        || 0;

  const activeHours = ALL_HOURS.filter(h => byHour[h] > 0);
  const hourRange   = activeHours.length > 0
    ? ALL_HOURS.slice(parseInt(activeHours[0]), parseInt(activeHours[activeHours.length - 1]) + 1)
    : [];
  const maxHourCount = Math.max(1, ...Object.values(byHour));

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{center.name}</div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{center.address}</div>
        </div>
        <div style={styles.totalBadge}>{total}</div>
      </div>

      <div style={styles.breakdownRow}>
        <BreakdownBlock title="By Gender"    data={byGender} labels={GENDER_LABELS} colors={GENDER_COLORS} total={total} />
        <BreakdownBlock title="By Age Group" data={byAge}    labels={AGE_LABELS}    colors={AGE_COLORS}    total={total} />
      </div>

      {hourRange.length > 0 && (
        <div>
          <div style={styles.sectionLabel}>Hourly Footfall (local time)</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
              <thead>
                <tr>
                  {hourRange.map(h => (
                    <th key={h} style={{ padding: "4px 10px", textAlign: "center", color: "#888", fontWeight: 500, borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}>
                      {h}:00
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {hourRange.map(h => {
                    const count = byHour[h] || 0;
                    const pct   = Math.round((count / maxHourCount) * 100);
                    return (
                      <td key={h} style={{ padding: "6px 10px", textAlign: "center", verticalAlign: "bottom", borderRight: "1px solid #f5f5f5" }}>
                        <div style={{ background: "#0093dd", borderRadius: "3px 3px 0 0", width: 20, height: Math.max(4, pct * 0.5), margin: "0 auto 4px", transition: "height 0.4s" }} />
                        <div style={{ fontSize: 13, fontWeight: count > 0 ? 600 : 400, color: count > 0 ? "#1a1a1a" : "#bbb" }}>{count}</div>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function BreakdownBlock({ title, data, labels, colors, total }) {
  return (
    <div style={styles.breakdownBlock}>
      <div style={styles.sectionLabel}>{title}</div>
      {Object.entries(labels).map(([key, label]) => {
        const count = data[key] || 0;
        const pct   = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <div key={key} style={{ marginBottom: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
              <span style={{ color: "#444" }}>{label}</span>
              <span style={{ fontWeight: 600 }}>{count} <span style={{ color: "#999", fontWeight: 400 }}>({pct}%)</span></span>
            </div>
            <div style={{ background: "#f0f0f0", borderRadius: 4, height: 6, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: colors[key] || "#0093dd", borderRadius: 4, transition: "width 0.4s" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString("en-CA");
}

const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    zIndex: 1000, padding: 16, overflowY: "auto",
  },
  panel: {
    background: "#f5f5f3", borderRadius: 16,
    width: "100%", maxWidth: 900,
    boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
    marginTop: 24, marginBottom: 24, overflow: "hidden",
  },
  header: {
    background: "#004d74", color: "#fff",
    padding: "14px 24px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    flexWrap: "wrap", gap: 10,
  },
  datePicker: {
    padding: "5px 8px", borderRadius: 7, border: "none",
    fontSize: 13, background: "rgba(255,255,255,0.15)", color: "#fff", cursor: "pointer",
  },
  closeBtn: {
    background: "none", border: "none",
    fontSize: 20, cursor: "pointer", color: "#fff", opacity: 0.8,
  },
  body: { padding: 20 },
  totalBanner: {
    background: "#fff", borderRadius: 12, padding: "16px 20px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    flexWrap: "wrap", gap: 12, marginBottom: 16,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  rangeBtn: (active) => ({
    padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
    border: active ? "2px solid #0093dd" : "2px solid #e0e0e0",
    background: active ? "#e5f5ff" : "#fafafa",
    color: active ? "#004d74" : "#555", fontWeight: active ? 700 : 400,
  }),
  empty: { textAlign: "center", color: "#888", padding: "40px 0", fontSize: 14 },
  searchInput: {
    width: "100%", padding: "10px 14px", marginBottom: 16,
    border: "1.5px solid #ddd", borderRadius: 8, fontSize: 14,
    boxSizing: "border-box", outline: "none",
  },
  pagination: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 0", marginTop: 4,
  },
  pageBtn: (disabled) => ({
    padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    border: "1.5px solid #ddd", background: disabled ? "#f5f5f5" : "#fff",
    color: disabled ? "#bbb" : "#333", cursor: disabled ? "not-allowed" : "pointer",
  }),
  card: {
    background: "#fff", borderRadius: 12, padding: 20, marginBottom: 16,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  cardHeader: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-start", marginBottom: 16,
  },
  totalBadge: {
    background: "#e5f5ff", color: "#004d74",
    fontWeight: 800, fontSize: 22, borderRadius: 10,
    padding: "4px 14px", minWidth: 48, textAlign: "center",
  },
  breakdownRow: { display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" },
  breakdownBlock: {
    flex: 1, minWidth: 180, background: "#fafafa", borderRadius: 8, padding: "12px 14px",
  },
  sectionLabel: {
    fontSize: 11, fontWeight: 700, color: "#888",
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10,
  },
};
